/**
 * Natural face behaviors for avatar lifelike animations
 *
 * Updated for the new shape-based expression system:
 * - Uses 'closed' shapes for blinking and mouth closing
 * - Works with shape names instead of full expression keys
 * - Simplified logic without size matching complexity
 *
 * These behaviors run continuously and independently of game state or user interactions,
 * creating lifelike "idle" animations that make the avatar feel breathing and alive.
 */

import { useCallback, useEffect, useRef } from 'react';
import { getBaseShape } from '@airhead-games/shared/avatar';

/**
 * Configuration for face behavior timing
 */
const FACE_BEHAVIOR_CONFIG = {
  // Blinking behavior
  BLINK: {
    INITIAL_DELAY_MIN: 1000, // 1 second minimum before first blink
    INITIAL_DELAY_MAX: 3000, // 3 seconds maximum before first blink
    INTERVAL_MIN: 4000, // 4 seconds minimum between blinks
    INTERVAL_MAX: 8000, // 8 seconds maximum between blinks
    DURATION_MIN: 120, // 120ms minimum blink duration
    DURATION_MAX: 180, // 180ms maximum blink duration
    EYE_OFFSET_MAX: 50, // Maximum 50ms offset between eyes
  },
  // Mouth closing behavior
  MOUTH_CLOSE: {
    INITIAL_DELAY_MIN: 5000, // 5 seconds minimum before first mouth close
    INITIAL_DELAY_MAX: 15000, // 15 seconds maximum before first mouth close
    INTERVAL_MIN: 10000, // 10 seconds minimum between mouth closes
    INTERVAL_MAX: 20000, // 20 seconds maximum between mouth closes
    DURATION_MIN: 1000, // 1 second minimum mouth close duration
    DURATION_MAX: 1500, // 1.5 seconds maximum mouth close duration
  },
} as const;

/**
 * Props for useAvatarFaceBehavior hook (updated for shape-based system)
 */
interface UseAvatarFaceBehaviorProps {
  eyeExpression: string; // Current eye shape name (e.g., 'arch', 'bean')
  mouthExpression: string; // Current mouth shape name (e.g., 'boat', 'fortunecookie')
  enableBlinking?: boolean; // Whether blinking is enabled
  enableMouthClosing?: boolean; // Whether mouth closing is enabled
  isPoppy?: boolean; // If Poppy (internally: no mouth closing)
  // Refs to the eye and mouth DOM elements for direct manipulation
  eyeRefs: {
    left: React.RefObject<SVGPathElement | null>;
    right: React.RefObject<SVGPathElement | null>;
  };
  mouthRef: React.RefObject<SVGPathElement | null>;
}

/**
 * Return type for useAvatarFaceBehavior hook
 */
interface UseAvatarFaceBehaviorReturn {
  triggerBlink: () => void; // Manually trigger a blink (useful for click interactions)
  triggerMouthClose: () => void; // Manually trigger a mouth close
}

/**
 * Hook for natural face behaviors (blinking, mouth movements)
 * Updated for the new shape-based expression system.
 *
 * @param props - Configuration for face behaviors
 * @returns Object with manual trigger functions
 */
export function useAvatarFaceBehavior({
  eyeExpression,
  mouthExpression,
  enableBlinking = true,
  enableMouthClosing = true,
  isPoppy = false,
  eyeRefs,
  mouthRef,
}: UseAvatarFaceBehaviorProps): UseAvatarFaceBehaviorReturn {
  // Timers for scheduling next behaviors
  const blinkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mouthTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lifecycleActiveRef = useRef(true);

  // Current shape paths for restoration after animations
  const currentEyePathRef = useRef<string>('');
  const currentMouthPathRef = useRef<string>('');

  // Guard against updates after unmount
  useEffect(() => {
    lifecycleActiveRef.current = true;
    return () => {
      lifecycleActiveRef.current = false;
    };
  }, []);

  // Track current shape paths for restoration after animations
  useEffect(() => {
    const eyeShape = getBaseShape('eye', eyeExpression);
    if (eyeShape) {
      currentEyePathRef.current = eyeShape.path;
    }
  }, [eyeExpression]);

  useEffect(() => {
    const mouthShape = getBaseShape('mouth', mouthExpression);
    if (mouthShape) {
      currentMouthPathRef.current = mouthShape.path;
    }
  }, [mouthExpression]);

  /**
   * Perform natural blinking animation using 'closed' eye shape
   *
   * Creates realistic blinking by:
   * - Using the 'closed' eye shape instead of sleepy expressions
   * - Randomly choosing which eye blinks first
   * - Adding slight timing offset between eyes for realism
   */
  const performBlink = useCallback(() => {
    const openPath = currentEyePathRef.current;
    if (!openPath) return;

    // Get the 'closed' eye shape for blinking
    const closedEyeShape = getBaseShape('eye', 'closed');
    const closedPath = closedEyeShape?.path;
    if (!closedPath) return;

    // Randomly pick which eye goes first for natural asymmetry
    const leftFirst = Math.random() < 0.5;
    const firstEyeRef = leftFirst ? eyeRefs.left : eyeRefs.right;
    const secondEyeRef = leftFirst ? eyeRefs.right : eyeRefs.left;

    // Random durations per eye
    const dur1 =
      FACE_BEHAVIOR_CONFIG.BLINK.DURATION_MIN +
      Math.random() *
        (FACE_BEHAVIOR_CONFIG.BLINK.DURATION_MAX -
          FACE_BEHAVIOR_CONFIG.BLINK.DURATION_MIN);
    const dur2 =
      FACE_BEHAVIOR_CONFIG.BLINK.DURATION_MIN +
      Math.random() *
        (FACE_BEHAVIOR_CONFIG.BLINK.DURATION_MAX -
          FACE_BEHAVIOR_CONFIG.BLINK.DURATION_MIN);
    const offset = Math.random() * FACE_BEHAVIOR_CONFIG.BLINK.EYE_OFFSET_MAX;

    // Blink first eye
    if (firstEyeRef.current) {
      firstEyeRef.current.setAttribute('d', closedPath);
      setTimeout(() => {
        if (lifecycleActiveRef.current && firstEyeRef.current) {
          firstEyeRef.current.setAttribute('d', openPath);
        }
      }, dur1);
    }

    // Blink second eye with slight offset for realism
    setTimeout(() => {
      if (secondEyeRef.current) {
        secondEyeRef.current.setAttribute('d', closedPath);
        setTimeout(() => {
          if (lifecycleActiveRef.current && secondEyeRef.current) {
            secondEyeRef.current.setAttribute('d', openPath);
          }
        }, dur2);
      }
    }, offset);
  }, [eyeRefs.left, eyeRefs.right]);

  /**
   * Perform subtle mouth closing animation using 'closed' mouth shape
   *
   * Creates natural mouth movements by temporarily closing the mouth
   * using the 'closed' mouth shape.
   */
  const performMouthClose = useCallback(() => {
    // Never close mouth for Poppy
    if (isPoppy) return;

    const openPath = currentMouthPathRef.current;
    if (!openPath || !mouthRef.current) return;

    // Get the 'closed' mouth shape for mouth closing
    const closedMouthShape = getBaseShape('mouth', 'closed');
    const closedPath = closedMouthShape?.path;
    if (!closedPath) return;

    // Close mouth
    mouthRef.current.setAttribute('d', closedPath);

    // Open mouth after close duration
    const duration =
      FACE_BEHAVIOR_CONFIG.MOUTH_CLOSE.DURATION_MIN +
      Math.random() *
        (FACE_BEHAVIOR_CONFIG.MOUTH_CLOSE.DURATION_MAX -
          FACE_BEHAVIOR_CONFIG.MOUTH_CLOSE.DURATION_MIN);
    setTimeout(() => {
      if (lifecycleActiveRef.current && mouthRef.current) {
        mouthRef.current.setAttribute('d', openPath);
      }
    }, duration);
  }, [mouthRef, isPoppy]);

  /**
   * Schedule the next blink with random timing
   */
  const scheduleNextBlink = useCallback(() => {
    if (blinkTimerRef.current) {
      clearTimeout(blinkTimerRef.current);
    }

    const delay =
      FACE_BEHAVIOR_CONFIG.BLINK.INTERVAL_MIN +
      Math.random() *
        (FACE_BEHAVIOR_CONFIG.BLINK.INTERVAL_MAX -
          FACE_BEHAVIOR_CONFIG.BLINK.INTERVAL_MIN);

    blinkTimerRef.current = setTimeout(() => {
      performBlink();
      scheduleNextBlink(); // Schedule next blink
    }, delay);
  }, [performBlink]);

  /**
   * Schedule the next mouth close with random timing
   */
  const scheduleNextMouthClose = useCallback(() => {
    if (mouthTimerRef.current) {
      clearTimeout(mouthTimerRef.current);
    }

    const delay =
      FACE_BEHAVIOR_CONFIG.MOUTH_CLOSE.INTERVAL_MIN +
      Math.random() *
        (FACE_BEHAVIOR_CONFIG.MOUTH_CLOSE.INTERVAL_MAX -
          FACE_BEHAVIOR_CONFIG.MOUTH_CLOSE.INTERVAL_MIN);

    mouthTimerRef.current = setTimeout(() => {
      performMouthClose();
      scheduleNextMouthClose(); // Schedule next mouth close
    }, delay);
  }, [performMouthClose]);

  // Start blinking behavior
  useEffect(() => {
    if (!enableBlinking) {
      if (blinkTimerRef.current) {
        clearTimeout(blinkTimerRef.current);
        blinkTimerRef.current = null;
      }
      return;
    }

    // Initial blink after random delay
    const initialDelay =
      FACE_BEHAVIOR_CONFIG.BLINK.INITIAL_DELAY_MIN +
      Math.random() *
        (FACE_BEHAVIOR_CONFIG.BLINK.INITIAL_DELAY_MAX -
          FACE_BEHAVIOR_CONFIG.BLINK.INITIAL_DELAY_MIN);

    blinkTimerRef.current = setTimeout(() => {
      performBlink();
      scheduleNextBlink(); // Start regular blinking schedule
    }, initialDelay);

    return () => {
      if (blinkTimerRef.current) {
        clearTimeout(blinkTimerRef.current);
        blinkTimerRef.current = null;
      }
    };
  }, [enableBlinking, performBlink, scheduleNextBlink]);

  // Start mouth closing behavior
  useEffect(() => {
    if (!enableMouthClosing || isPoppy) {
      if (mouthTimerRef.current) {
        clearTimeout(mouthTimerRef.current);
        mouthTimerRef.current = null;
      }
      return;
    }

    // Initial mouth close after random delay
    const initialDelay =
      FACE_BEHAVIOR_CONFIG.MOUTH_CLOSE.INITIAL_DELAY_MIN +
      Math.random() *
        (FACE_BEHAVIOR_CONFIG.MOUTH_CLOSE.INITIAL_DELAY_MAX -
          FACE_BEHAVIOR_CONFIG.MOUTH_CLOSE.INITIAL_DELAY_MIN);

    mouthTimerRef.current = setTimeout(() => {
      performMouthClose();
      scheduleNextMouthClose(); // Start regular mouth closing schedule
    }, initialDelay);

    return () => {
      if (mouthTimerRef.current) {
        clearTimeout(mouthTimerRef.current);
        mouthTimerRef.current = null;
      }
    };
  }, [enableMouthClosing, isPoppy, performMouthClose, scheduleNextMouthClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (blinkTimerRef.current) {
        clearTimeout(blinkTimerRef.current);
      }
      if (mouthTimerRef.current) {
        clearTimeout(mouthTimerRef.current);
      }
    };
  }, []);

  return {
    triggerBlink: performBlink,
    triggerMouthClose: performMouthClose,
  };
}
